import { useCallback, useRef } from 'react'
import type { Node, Edge } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { bringNodeToFront } from '../Nodes/hooks/useNodeZIndex'
import { getDescendantNodeIds } from '../../../utils/collapseUtils'

type SetNodes = React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>

interface UseNodeDragHandlersDeps {
  edges: Edge[]
  setNodes: SetNodes
}

export function useNodeDragHandlers({ edges, setNodes }: UseNodeDragHandlersDeps) {
  const dragStartRef = useRef<{ nodeId: string; position: { x: number; y: number } } | null>(null)

  // Handle node drag start - bring node to front and track position
  const handleNodeDragStart = useCallback(
    (_event: React.MouseEvent, node: Node<ReactFlowNodeData>) => {
      // Use centralized utility function for consistency
      bringNodeToFront(node.id, setNodes)
      // Track position for moving descendants in real-time
      dragStartRef.current = {
        nodeId: node.id,
        position: { x: node.position.x, y: node.position.y },
      }
    },
    [setNodes]
  )

  // Handle node drag - move all descendants in real-time.
  // Default: descendants follow the parent.
  // Shift+drag: parent moves independently (detaches from its subtree).
  const handleNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node<ReactFlowNodeData>) => {
      if (!dragStartRef.current || dragStartRef.current.nodeId !== node.id) {
        return
      }

      // Calculate delta from last known position
      const deltaX = node.position.x - dragStartRef.current.position.x
      const deltaY = node.position.y - dragStartRef.current.position.y

      // Update the ref with current position for next drag event
      dragStartRef.current.position = { x: node.position.x, y: node.position.y }

      // If no movement, do nothing
      if (deltaX === 0 && deltaY === 0) return

      // Shift+drag = move parent only (detach from subtree)
      if (event.shiftKey) return

      // Get all descendant node IDs (children, grandchildren, etc.)
      const descendantIds = getDescendantNodeIds(node.id, edges)

      // If no descendants, nothing to move
      if (descendantIds.length === 0) {
        return
      }

      // Move all descendants by the same delta
      setNodes((prevNodes) => {
        return prevNodes.map((n) => {
          if (descendantIds.includes(n.id) && !n.hidden) {
            return {
              ...n,
              position: {
                x: n.position.x + deltaX,
                y: n.position.y + deltaY,
              },
            }
          }
          return n
        })
      })
    },
    [edges, setNodes]
  )

  // Handle node drag stop - clear tracking and mark as unsaved
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node<ReactFlowNodeData>) => {
      if (dragStartRef.current?.nodeId === node.id) {
        dragStartRef.current = null
      }
    },
    []
  )

  return { handleNodeDragStart, handleNodeDrag, handleNodeDragStop }
}
